import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  structuredData?: Record<string, unknown>[];
};

const siteUrl = 'https://uu.me';
const defaultImage = `${siteUrl}/uumail.png`;

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element?.setAttribute(name, value);
  });
};

const upsertCanonical = (href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const removeStructuredData = () => {
  document.head.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach((element) => {
    element.remove();
  });
};

const Seo = ({ title, description, path = '/', structuredData = [] }: SeoProps) => {
  useEffect(() => {
    const canonicalPath = path === '/' ? '/' : `${path.replace(/\/$/, '')}/`;
    const canonical = `${siteUrl}${canonicalPath === '/' ? '/' : canonicalPath}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: defaultImage });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: defaultImage });
    upsertCanonical(canonical);

    removeStructuredData();
    structuredData.forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seo = 'true';
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });

    return removeStructuredData;
  }, [description, path, structuredData, title]);

  return null;
};

export default Seo;
